import { Component, OnInit, Input, Output, EventEmitter, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  InspectionAppConfigurationService,
  FieldKeyItem,
  FieldKeyItemSource,
  HomePageColumnMapping,
  HomePageColumnConfiguration
} from '../../../../services/inspection-app-configuration.service';
import { ModalConfirmComponent } from '../../../../components/system/modal-confirm/modal-confirm.component';
import { ToastrService } from 'ngx-toastr';
import { WorkflowContextService } from 'src/app/services';

interface WorkingMapping {
  fieldKey: string;
  source: FieldKeyItemSource;
}

interface WorkingColumn {
  label: string;
  mappings: WorkingMapping[];
}

interface ColumnSection {
  columns: WorkingColumn[];
  newColumnLabel: string;
  pendingDeleteIndex: number | null;
}

@Component({
  selector: 'app-inspection-app-configuration',
  templateUrl: './inspection-app-configuration.component.html',
  styleUrls: ['./inspection-app-configuration.component.scss'],
  standalone: false
})
export class InspectionAppConfigurationComponent implements OnInit {
  @Input() clientId: string;
  @Input() departmentId?: string;
  @Output() saved = new EventEmitter<void>();

  @ViewChild('deleteColumnModal') deleteColumnModal: ModalConfirmComponent;
  @ViewChild('cancelModal') cancelModal: ModalConfirmComponent;
  @ViewChild('deleteConfigModal') deleteConfigModal: ModalConfirmComponent;

  availableFields: FieldKeyItem[] = [];
  isDirty = false;
  isLoading = false;

  homePage: ColumnSection = this.createEmptySection();
  appDetails: ColumnSection = this.createEmptySection();

  private pendingDeleteSection: ColumnSection | null = null;

  // expose enum to template
  FieldKeyItemSource = FieldKeyItemSource;

  constructor(
    private _route: ActivatedRoute,
    private _iacSvc: InspectionAppConfigurationService,
    private _toastr: ToastrService,
    private _context: WorkflowContextService
  ) {}

  ngOnInit(): void {
    if (!this.clientId && this._context.client.id) {
      this.clientId = this._context.client.id;
    }

    if (!this.departmentId && this._context.staffDepartmentId) {
      this.departmentId = this._context.staffDepartmentId;
    }

    this.loadConfiguration();
  }

  private createEmptySection(): ColumnSection {
    return {
      columns: [],
      newColumnLabel: '',
      pendingDeleteIndex: null
    };
  }

  private toWorkingColumns(config: { [key: number]: HomePageColumnConfiguration } | undefined | null): WorkingColumn[] {
    if (!config || Object.keys(config).length === 0) {
      return [];
    }

    return Object.keys(config)
      .sort((a, b) => parseInt(a) - parseInt(b))
      .map(key => {
        const c = config[parseInt(key)];
        return {
          label: c.label ?? '',
          mappings: (c.mappings ?? [])
            .sort((a, b) => a.priority - b.priority)
            .map(m => ({ fieldKey: m.fieldKey, source: m.source }))
        } as WorkingColumn;
      });
  }

  private toConfigurationDict(columns: WorkingColumn[]): { [key: number]: HomePageColumnConfiguration } {
    const dict: { [key: number]: HomePageColumnConfiguration } = {};
    columns.forEach((col, colIdx) => {
      dict[colIdx + 1] = {
        label: col.label,
        mappings: col.mappings.map((m, mIdx) => ({
          priority: mIdx + 1,
          fieldKey: m.fieldKey,
          source: m.source
        }))
      };
    });
    return dict;
  }

  loadConfiguration(): void {
    this.isLoading = true;

    this._iacSvc.getAvailableFields(this.clientId, this.departmentId).subscribe(
      response => {
        this.availableFields = response.availableFields ?? [];

        this._iacSvc.getConfiguration(this.clientId, this.departmentId).subscribe(
          configResponse => {
            this.homePage.columns = this.toWorkingColumns(configResponse.homePageColumns);
            this.homePage.newColumnLabel = '';
            this.homePage.pendingDeleteIndex = null;

            this.appDetails.columns = this.toWorkingColumns(configResponse.applicationDetails);
            this.appDetails.newColumnLabel = '';
            this.appDetails.pendingDeleteIndex = null;

            this.isDirty = false;
            this.isLoading = false;
          },
          error => {
            this._toastr.error('Failed to load configuration');
            this.isLoading = false;
          }
        );
      },
      error => {
        this._toastr.error('Failed to load available fields');
        this.isLoading = false;
      }
    );
  }

  addColumn(section: ColumnSection): void {
    if (!section.newColumnLabel.trim()) {
      this._toastr.warning('Please enter a label');
      return;
    }

    section.columns.push({ label: section.newColumnLabel.trim(), mappings: [] });
    section.newColumnLabel = '';
    this.isDirty = true;
  }

  deleteColumn(section: ColumnSection, index: number): void {
    section.pendingDeleteIndex = index;
    this.pendingDeleteSection = section;
    this.deleteColumnModal.open();
  }

  confirmDeleteColumn(): void {
    const section = this.pendingDeleteSection;
    if (section !== null && section.pendingDeleteIndex !== null) {
      section.columns.splice(section.pendingDeleteIndex, 1);
      section.pendingDeleteIndex = null;
      this.isDirty = true;
    }
    this.pendingDeleteSection = null;
  }

  addFieldMapping(section: ColumnSection, columnIndex: number, field: FieldKeyItem): void {
    if (!field) return;
    section.columns[columnIndex].mappings.push({ fieldKey: field.fieldKey, source: field.source });
    this.isDirty = true;
  }

  hasAnyAvailableFields(section: ColumnSection, columnIndex: number): boolean {
    const usedKeysInColumn = new Set(section.columns[columnIndex].mappings.map(m => m.fieldKey));
    return this.availableFields.some(f => !usedKeysInColumn.has(f.fieldKey));
  }

  removeMapping(section: ColumnSection, columnIndex: number, mappingIndex: number): void {
    section.columns[columnIndex].mappings.splice(mappingIndex, 1);
    this.isDirty = true;
  }

  dropColumn(event: CdkDragDrop<WorkingColumn[]>, section: ColumnSection): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(section.columns, event.previousIndex, event.currentIndex);
      this.isDirty = true;
    }
  }

  dropMapping(event: CdkDragDrop<WorkingMapping[]>, section: ColumnSection, columnIndex: number): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(section.columns[columnIndex].mappings, event.previousIndex, event.currentIndex);
      this.isDirty = true;
    }
  }

  save(): void {
    this.isLoading = true;

    const homePageDict = this.toConfigurationDict(this.homePage.columns);
    const appDetailsDict = this.toConfigurationDict(this.appDetails.columns);

    this._iacSvc.saveConfiguration(this.clientId, this.departmentId, homePageDict, appDetailsDict).subscribe(
      response => {
        this._toastr.success('Configuration saved successfully');
        this.isDirty = false;
        this.isLoading = false;
        this.saved.emit();
      },
      error => {
        this._toastr.error('Failed to save configuration');
        this.isLoading = false;
      }
    );
  }

  cancel(): void {
    if (this.isDirty) {
      this.cancelModal.open();
    } else {
      this.loadConfiguration();
    }
  }

  confirmCancel(): void {
    this.loadConfiguration();
  }

  delete(): void {
    this.deleteConfigModal.open();
  }

  confirmDelete(): void {
    this.isLoading = true;

    this._iacSvc.deleteConfiguration(this.clientId, this.departmentId).subscribe(
      response => {
        this._toastr.success('Configuration deleted successfully');
        this.homePage = this.createEmptySection();
        this.appDetails = this.createEmptySection();
        this.isDirty = false;
        this.isLoading = false;
        this.saved.emit();
      },
      error => {
        this._toastr.error('Failed to delete configuration');
        this.isLoading = false;
      }
    );
  }

  hasAnyConfiguration(): boolean {
    return this.homePage.columns.length > 0 || this.appDetails.columns.length > 0;
  }

  getFieldsForMapping(section: ColumnSection, columnIndex: number, source: FieldKeyItemSource): FieldKeyItem[] {
    const usedKeysInColumn = new Set(section.columns[columnIndex].mappings.map(m => m.fieldKey));
    return this.availableFields.filter(f => f.source === source && !usedKeysInColumn.has(f.fieldKey));
  }

  getFieldLabel(fieldKey: string): string {
    return this.availableFields.find(f => f.fieldKey === fieldKey)?.label ?? fieldKey;
  }

  getConfiguredFieldLabel(fieldKey: string, source: FieldKeyItemSource): string {
    const label = this.getFieldLabel(fieldKey);
    if (source !== FieldKeyItemSource.InspectionCustomFields || label === fieldKey) {
      return label;
    }

    return `${label} (${fieldKey})`;
  }

  getSourceLabel(source: FieldKeyItemSource): string {
    switch (source) {
      case FieldKeyItemSource.Application:            return 'System Application Field';
      case FieldKeyItemSource.Inspection:             return 'System Inspection Field';
      case FieldKeyItemSource.InspectionCustomFields: return 'Custom Inspection Field';
      default: return 'Unknown';
    }
  }

  getInspectionCustomFieldOptionLabel(field: FieldKeyItem): string {
    const label = field.label?.trim() || field.fieldKey;
    if (label === field.fieldKey) {
      return field.fieldKey;
    }

    return `${label} (${field.fieldKey})`;
  }
}
